import Icon from "../../../../components/ui/Icon";
import { FC, useContext, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import FormFieldsContainer from "../../../../components/ui/FormFieldsContainer";
import {
  AddIcon,
  Button,
  FlexContent,
  Form,
  FormField,
  InlineOrderEditor,
  Input,
  ModalDialog,
  Select,
  TableCell,
  TableHeadCell,
  TableRow,
} from "@rothoblaas/design-system";
import Table from "../../../../components/ui/Table";
import { getDeleteAction, getEditAction } from "../../../../utils/rowActions";
import Visibility from "../../../../components/ui/Visibility";
import VisibilityScope from "../../../../components/ui/VisibilityScope";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../context/notificationContext";
import useInitializer from "../../../../hooks/useInitializer";
import useListFilters from "../../../../hooks/useListFilters";
import useI18n from "../../../../hooks/useTranslations";
import { AdminStandard, NormativeType, NormativeTypes } from "../../../../http/lock_calculator";
import { useAdminLockCalculatorApi } from "../../../../http/lock_calculator/useAdminLockCalculator";
import { PaginationQuery } from "../../../../types";

interface StandardFormBody {
  code: string;
  name: string;
  normativeType: NormativeType | "";
  jurisdictionCode: string;
  visibility: "public" | "internal" | "hidden" | null;
}

const AdminStandardsTab: FC = () => {
  const i18n = useI18n();
  const adminApi = useAdminLockCalculatorApi();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ isSearchable: true, prefix: "standards" });
  const aborter = useRef(new AbortController());
  const debouncedValue = filters.state.q;
  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = adminApi.useStandards(query, aborter.current.signal);
  const isInitialized = useInitializer(data || error);

  const [itemToDelete, setItemToDelete] = useState<AdminStandard>();
  const [itemToEdit, setItemToEdit] = useState<AdminStandard>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateVisibility = async (item: AdminStandard, _visibility: "public" | "internal" | "hidden") => {
    try {
      await adminApi.updateStandard(item.id, { visibility: _visibility });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (id: string, newOrder: number) => {
    try {
      await adminApi.updateStandardOrder(id, { order: newOrder });
      dispatch(showSuccessNotification(i18n.translation));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  if (!data && !error && !isInitialized) return null;

  return (
    <>
      <FlexContent padding>
        <Table<AdminStandard>
          filters={filters}
          search={{
            placeholder: i18n.translation.common.search + "...",
            onChange: () => {
              aborter.current.abort();
              aborter.current = new AbortController();
            },
          }}
          headingActions={
            <Button onClick={() => setIsCreateOpen(true)}><Icon glyph={AddIcon} />
              {i18n.translation.common.add}
            </Button>
          }
          head={
            <TableRow>
              <TableHeadCell align="left">{i18n.translation.common.code}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.name}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.standards.normativeType}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.lockCalculatorModule.admin.standards.jurisdictionCode}</TableHeadCell>
              <TableHeadCell align="left">{i18n.translation.common.visibility}</TableHeadCell>
              <TableHeadCell align="center">{i18n.translation.common.order}</TableHeadCell>
              <TableHeadCell align="left"></TableHeadCell>
            </TableRow>
          }
          renderRow={(item) => (
            <>
              <TableCell>{item.code}</TableCell>
              <TableCell>{item.name}</TableCell>
              <TableCell>{i18n.translation.lockCalculatorModule.admin.standards.normativeTypes[item.normativeType]}</TableCell>
              <TableCell>{item.jurisdictionCode ?? i18n.translation.lockCalculatorModule.admin.emptyValue}</TableCell>
              <TableCell>
                {item.visibility != null && (
                  <Visibility
                    value={item.visibility as "public" | "internal" | "hidden"}
                    onChange={(nextVisibility) => updateVisibility(item, nextVisibility)}
                  />
                )}
              </TableCell>
              <TableCell align="center"><InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} /></TableCell>
            </>
          )}
          onRowClick={(item) => setItemToEdit(item)}
          rowActions={(item) => [
            getEditAction(i18n, () => setItemToEdit(item)),
            getDeleteAction(i18n, () => setItemToDelete(item), true),
          ]}
          items={data?.items}
          total={data?.total}
          error={!!error}
        />

        {!!itemToDelete && (
          <ModalDialog
            onClose={() => setItemToDelete(undefined)}
            title={i18n.translation.lockCalculatorModule.admin.standards.singular}
            isSubmitting={isSubmitting}
            buttons={[<Button key={0} onClick={() => setItemToDelete(undefined)} variant="secondary">{i18n.translation.common.cancel}</Button>, <Button key={1} onClick={() => {
                  setIsSubmitting(true);
                  adminApi
                    .deleteStandard(itemToDelete.id)
                    .then(() => {
                      dispatch(showSuccessNotification(i18n.translation));
                      setItemToDelete(undefined);
                    })
                    .catch((err) => dispatch(showErrorNotification(i18n, err)))
                    .finally(() => setIsSubmitting(false));
                }}>{i18n.translation.common.yes}</Button>]}
          >
            {i18n.translation.clients.questions.delete}
          </ModalDialog>
        )}
      </FlexContent>

      {isCreateOpen && <StandardFormModal onClose={() => setIsCreateOpen(false)} />}
      {!!itemToEdit && <StandardFormModal item={itemToEdit} onClose={() => setItemToEdit(undefined)} />}
    </>
  );
};

const StandardFormModal: FC<{ item?: AdminStandard; onClose: () => void }> = ({ item, onClose }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const adminApi = useAdminLockCalculatorApi();

  const normativeTypeOptions = NormativeTypes.map((value) => ({
    value,
    label: i18n.translation.lockCalculatorModule.admin.standards.normativeTypes[value],
  }));

  const defaultValues: StandardFormBody = {
    code: item?.code ?? "",
    name: item?.name ?? "",
    normativeType: item?.normativeType ?? "",
    jurisdictionCode: item?.jurisdictionCode ?? "",
    visibility: (item?.visibility as "public" | "internal" | "hidden") ?? "hidden",
  };

  const onSubmit = () => async (formData: StandardFormBody) => {
    const jurisdictionCode = formData.jurisdictionCode || null;
    const visibility = formData.visibility ?? null;
    try {
      if (item) {
        await adminApi.updateStandard(item.id, {
          code: formData.code,
          name: formData.name,
          normativeType: formData.normativeType || null,
          jurisdictionCode,
          visibility,
        });
      } else {
        await adminApi.insertStandard({
          code: formData.code,
          name: formData.name,
          normativeType: formData.normativeType as NormativeType,
          jurisdictionCode,
          visibility,
        });
      }
      dispatch(showSuccessNotification(i18n.translation));
      onClose();
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  return (
    <ModalDialog
      onClose={onClose}
      title={item ? `${i18n.translation.common.edit} ${i18n.translation.lockCalculatorModule.admin.standards.singular}` : `${i18n.translation.common.add} ${i18n.translation.lockCalculatorModule.admin.standards.singular}`}
    >
      <Form<StandardFormBody>
        submitText={i18n.translation.common.save}
        onSubmit={onSubmit}
       
        defaultValues={defaultValues}
        secondaryButton={
          <Button onClick={onClose} variant="secondary">
            {i18n.translation.common.cancel}
          </Button>
        }
      >
        {({ control }) => (
          <FormFieldsContainer fullWidth>
            <Controller
              name="code"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.code} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="name"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.name} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="normativeType"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => {
                const selectedOption = normativeTypeOptions.find((option) => option.value === field.value) ?? null;
                return (
                  <FormField label={i18n.translation.lockCalculatorModule.admin.standards.normativeType} error={fieldState.error}>
                    {({ isInvalid }) => (
                      <Select
                        options={normativeTypeOptions}
                        value={selectedOption}
                        isInvalid={isInvalid}
                        getOptionValue={(option) => option.value}
                        getOptionLabel={(option) => option.label}
                        onChange={(option) => field.onChange(option?.value ?? "")}
                      />
                    )}
                  </FormField>
                );
              }}
            />
            <Controller
              name="jurisdictionCode"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.lockCalculatorModule.admin.standards.jurisdictionCode} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Input id={labelId} value={field.value} isInvalid={isInvalid} onChange={field.onChange} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error}>
                  {() => <VisibilityScope value={field.value} onChange={(value) => field.onChange(value)} />}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        )}
      </Form>
    </ModalDialog>
  );
};

export default AdminStandardsTab;
